import { formatApiDateTimeDisplay } from './date'
import { resolveApiAssetUrl } from './api'

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function hiddenLookupStatusLabel(status) {
  switch (status) {
    case 'COMPLETED':
      return '조회 완료'
    case 'FAILED':
      return '조회 실패'
    case 'AUTH_WAITING':
    case 'AUTH_REQUESTED':
      return '인증 대기'
    case 'RUNNING':
    case 'ANALYSIS_RUNNING':
      return '조회 중'
    default:
      return status || '-'
  }
}

function daysUntilYmd(value) {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const target = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  if (Number.isNaN(target.getTime())) return null
  const today = new Date()
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.ceil((target.getTime() - base.getTime()) / 86400000)
}

function lineText(line) {
  if (line == null) return ''
  if (typeof line === 'object') {
    return line.title || line.label || line.message || line.summary || ''
  }
  return String(line)
}

function lineList(lines, fallback) {
  const cleaned = (Array.isArray(lines) ? lines : [])
    .map((line) => lineText(line).replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  return (cleaned.length ? cleaned : fallback).slice(0, 4)
}

function dateText(row) {
  if (!row) return '-'
  const formatted = formatApiDateTimeDisplay(row.createdAtDisplay || row.reportDate, row.createdAt)
  return formatted && formatted !== '-' ? formatted.slice(0, 10) : '-'
}

function formatWonText(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return `${Math.round(n).toLocaleString('ko-KR')}원`
}

function readStoredUser() {
  try {
    const raw = window?.localStorage?.getItem?.('auth.user')
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || '').trim()
    if (text) return text
  }
  return ''
}

function resolveAdvisor(advisorRaw, customerName) {
  const storedUser = readStoredUser()
  const advisor = advisorRaw && typeof advisorRaw === 'object' ? advisorRaw : {}
  const email = firstNonEmpty(advisor.email, storedUser.email, storedUser.username)
  const fallbackName = email && email.includes('@') ? email.split('@')[0] : ''
  const profileImage = firstNonEmpty(
    advisor.profileImageUrl,
    advisor.profileImageFileId,
    advisor.profile_image_url,
    advisor.profile_image_file_id,
    storedUser.profileImageUrl,
    storedUser.profileImageFileId,
    storedUser.profile_image_url,
    storedUser.profile_image_file_id,
  )
  return {
    name: firstNonEmpty(advisor.name, advisor.displayName, advisor.userName, storedUser.name, storedUser.displayName, storedUser.userName, fallbackName, '담당 설계사'),
    title: firstNonEmpty(advisor.title, advisor.jobTitle, advisor.position, storedUser.title, storedUser.jobTitle, storedUser.position, '담당 설계사'),
    orgName: firstNonEmpty(advisor.orgName, advisor.organizationName, storedUser.orgName, storedUser.organizationName, storedUser.companyName),
    phone: firstNonEmpty(advisor.phoneNumber, advisor.mobilePhone, advisor.phone, storedUser.phoneNumber, storedUser.mobilePhone, storedUser.phone),
    profileImageUrl: profileImage ? resolveApiAssetUrl(profileImage) : '',
    message: firstNonEmpty(
      advisor.message,
      `${customerName}님께 필요한 확인사항을 함께 정리해드리겠습니다.`,
    ),
  }
}

function advisorCard(advisor) {
  return {
    type: 'advisor',
    title: `${advisor.name} 설계사`,
    badge: '담당 설계사',
    lines: [
      advisor.message,
      advisor.orgName ? `${advisor.orgName} · ${advisor.title}` : advisor.title,
      advisor.phone ? `연락처 ${advisor.phone}` : '궁금한 점은 담당 설계사에게 편하게 문의해 주세요.',
    ].filter(Boolean),
    advisor,
  }
}

export function buildCustomerCardNewsHtml(data = {}) {
  const sourceType = data.sourceType || 'integrated'
  const customer = data.customer || {}
  const customerName = customer.customerName || customer.name || data.customerName || '고객'
  const advisor = resolveAdvisor(data.advisor, customerName)
  const health = data.latestHealth || {}
  const hidden = data.latestHiddenInsurance || {}
  const coverageContracts = Array.isArray(data.coverageContracts) ? data.coverageContracts : []
  const maturityRows = Array.isArray(data.maturityRows) ? data.maturityRows : []
  const contractCount = coverageContracts.length
  const report = data.integratedReportDraft || {}
  const aiNarrative = report?.aiNarrative?.status === 'SUCCESS' ? report.aiNarrative : {}
  const aiCardNewsCopy = Array.isArray(aiNarrative.cardNewsCopy) ? aiNarrative.cardNewsCopy.filter(Boolean).slice(0, 4) : []
  const review = report.standardCoverageReview || {}
  const priorityRows = Array.isArray(review.priorityRows)
    ? review.priorityRows
    : Array.isArray(review.rows)
      ? review.rows.filter((row) => row.status && row.status !== 'ok')
      : []
  const nearestMaturityRows = [...maturityRows]
    .filter((row) => row && row.nextMaturityDate)
    .sort((a, b) => String(a.nextMaturityDate).localeCompare(String(b.nextMaturityDate)))
    .slice(0, 3)
  const shortageLines = priorityRows.slice(0, 3).map((row) => {
    const label = row.label || row.coverageLabel || row.category || '보장 항목'
    const current = row.currentAmountText || row.insuredAmountText || '-'
    const target = row.targetText || row.referenceText || '-'
    return `${label}: 현재 ${current} / 기준 ${target}`
  })
  const integratedCards = () => {
    if (aiCardNewsCopy.length) {
      return [
        ...aiCardNewsCopy.slice(0, 3).map((copy) => ({
          title: copy.title || `${customerName}님 통합분석 핵심`,
          badge: copy.section === 'integrated' ? '통합분석' : '확인사항',
          lines: lineList([copy.body], ['현재 분석 결과에서 확인할 핵심 내용을 정리했습니다.']),
        })),
        advisorCard(advisor),
      ]
    }
    const keyMessages = lineList(report.keyMessages, [
      contractCount ? `보유계약 ${contractCount}건을 기준으로 보장 현황을 확인했습니다.` : '보유계약 정보가 추가되면 더 정확한 분석이 가능합니다.',
      health?.id ? `진료정보분석 결과를 함께 확인했습니다.` : '진료정보가 있으면 최근 건강 이슈를 함께 살펴볼 수 있습니다.',
      hidden?.id ? `숨은보험금 조회 결과를 함께 확인했습니다.` : '숨은보험금 조회가 있으면 놓친 청구 가능성을 함께 확인할 수 있습니다.',
    ])
    return [
      { title: `${customerName}님 통합분석 핵심`, badge: '통합분석', lines: keyMessages },
      { title: '추가 확인하면 좋은 항목', badge: '확인사항', lines: shortageLines.length ? shortageLines : ['보장금액과 보험료 부담이 현재 상황에 맞는지 확인해 주세요.', '만기·갱신이 가까운 계약은 조건 변경 여부를 함께 확인하면 좋습니다.'] },
      advisorCard(advisor),
    ]
  }
  const healthCards = () => [
    {
      title: `${customerName}님 진료정보분석 요약`,
      badge: '진료정보분석',
      lines: health?.id
        ? [
          `분석일 ${dateText(health)}`,
          health.riskLabel || health.riskLevel ? `참고 위험도 ${health.riskLabel || health.riskLevel}` : '최근 진료·검사·투약 흐름을 확인했습니다.',
          health.riskScore != null ? `분석 점수 ${health.riskScore}` : '진료 이력의 반복 여부와 치료 흐름을 확인했습니다.',
        ]
        : ['아직 연결된 진료정보분석 결과가 없습니다.', '분석 후 고객에게 공유할 핵심 카드뉴스를 만들 수 있습니다.'],
    },
    { title: '확인해주시면 좋은 사항', badge: '확인사항', lines: ['최근 추가검사·재검사 안내를 받은 적이 있는지 확인해 주세요.', '현재 치료가 끝났는지, 계속 치료 중인지 확인해 주세요.', '복용 중인 약이 있다면 약 이름과 복용 기간을 확인해 주세요.'] },
    advisorCard(advisor),
  ]
  const hiddenResultLines = () => {
    if (!hidden?.id) return ['아직 연결된 숨은보험금 조회 이력이 없습니다.', '조회 후 고객에게 공유할 핵심 카드뉴스를 만들 수 있습니다.']
    const lines = [`조회일 ${dateText(hidden)}`]
    if (hidden.estimatedClaimable != null) lines.push(`추정 청구 가능액 ${formatWonText(hidden.estimatedClaimable)}`)
    if (hidden.reimbursementTotal != null) lines.push(`실제 실손 수령액 ${formatWonText(hidden.reimbursementTotal)}`)
    if (hidden.unclaimedHint != null) lines.push(`미청구 가능성 금액 ${formatWonText(hidden.unclaimedHint)}`)
    if (lines.length === 1) {
      lines.push(`조회상태 ${hiddenLookupStatusLabel(hidden.status)}`)
      lines.push(hidden.status === 'COMPLETED' ? '놓친 청구 가능성을 확인했습니다.' : '조회가 완료되면 청구 가능 항목을 정리할 수 있습니다.')
    }
    return lines
  }
  const hiddenCards = () => [
    {
      title: `${customerName}님 숨은보험금 조회 요약`,
      badge: '숨은보험금찾기',
      lines: hiddenResultLines(),
    },
    { title: '확인해주시면 좋은 사항', badge: '확인사항', lines: ['최근 진료비 영수증과 세부내역서가 남아 있는지 확인해 주세요.', '이미 보험금 청구를 한 진료인지 확인해 주세요.', '처방전이나 진단명 확인 서류가 있으면 함께 준비해 주세요.'] },
    advisorCard(advisor),
  ]
  const maturityCards = () => [
    { title: `${customerName}님 만기·갱신 요약`, badge: '보험만기', lines: maturityRows.length ? [`확인된 만기·갱신 계약 ${maturityRows.length}건`, nearestMaturityRows[0] ? `가장 빠른 만기 ${nearestMaturityRows[0].nextMaturityDate}` : '만기일 확인 필요', '갱신 전 보험료와 보장 조건을 미리 비교하면 좋습니다.'] : ['확인된 만기·갱신 계약이 없습니다.', '계약 정보가 확인되면 만기 전 점검 사항을 안내드릴 수 있습니다.'] },
    { title: '가까운 만기 일정', badge: 'D-day', lines: nearestMaturityRows.length ? nearestMaturityRows.map((row) => {
      const dday = daysUntilYmd(row.nextMaturityDate)
      const ddayText = dday == null ? 'D-day 확인 필요' : dday === 0 ? 'D-day' : `D-${dday}`
      return `${row.nextMaturityDate} ${ddayText} · ${row.insurerName || '보험사 미입력'} ${row.productLabel || row.vehicleNumber || ''}`
    }) : ['가까운 만기 일정이 확인되면 미리 안내드리겠습니다.'] },
    { title: '확인해주시면 좋은 사항', badge: '확인사항', lines: ['갱신 후 보험료가 달라지는지 확인해 주세요.', '운전자 범위, 차량번호, 담보 조건 변경 여부를 확인해 주세요.', '만기 전 유지·변경 여부를 미리 정하면 불필요한 공백을 줄일 수 있습니다.'] },
    advisorCard(advisor),
  ]
  const cards = sourceType === 'health'
    ? healthCards()
    : sourceType === 'hidden'
      ? hiddenCards()
      : sourceType === 'maturity'
        ? maturityCards()
        : integratedCards()
  const deckTitle = sourceType === 'health'
    ? '진료정보분석 카드뉴스'
    : sourceType === 'hidden'
      ? '숨은보험금찾기 카드뉴스'
      : sourceType === 'maturity'
        ? '보험만기 카드뉴스'
        : '통합리포팅 카드뉴스'
  const cardNewsPayload = JSON.stringify({
    title: deckTitle,
    sourceType,
    cards: cards.map((card) => ({
      title: card.title || '',
      badge: card.badge || '',
      type: card.type || '',
      advisor: card.advisor || null,
      lines: (Array.isArray(card.lines) ? card.lines : []).map((line) => String(line || '')),
    })),
  }).replace(/</g, '\\u003c')

  return `<!doctype html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(deckTitle)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; background: #e5e7eb; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans KR", sans-serif; color: #0f172a; }
    .toolbar { position: sticky; top: 0; z-index: 10; display: flex; justify-content: space-between; align-items: center; gap: 12px; background: rgba(255,255,255,.96); border-bottom: 1px solid #dbe3ef; padding: 12px 18px; box-shadow: 0 8px 22px rgba(15,23,42,.08); }
    .toolbar-title { display: grid; gap: 2px; min-width: 0; }
    .toolbar strong { font-size: 14px; }
    .toolbar span { color: #64748b; font-size: 12px; font-weight: 700; }
    .toolbar-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
    button { border: 1px solid #cbd5e1; background: #fff; color: #0f172a; cursor: pointer; font-weight: 800; font-size: 12px; padding: 8px 12px; border-radius: 8px; }
    button.primary { border-color: #0f2a4d; background: #0f2a4d; color: #fff; }
    button:disabled { opacity: .45; cursor: not-allowed; }
    .viewer { min-height: calc(100vh - 58px); display: grid; grid-template-columns: 104px minmax(320px, 680px) 104px; align-items: center; justify-content: center; gap: 18px; padding: 22px; }
    .stage { display: grid; gap: 12px; justify-items: center; }
    .card-host { width: min(88vw, 620px); aspect-ratio: 1 / 1; border-radius: 16px; overflow: hidden; box-shadow: 0 22px 64px rgba(15,23,42,.22); background: #fff; }
    .card-host svg { width: 100%; height: 100%; display: block; }
    .nav { width: 88px; min-height: 72px; border-radius: 12px; background: #fff; box-shadow: 0 12px 32px rgba(15,23,42,.12); }
    .counter { color: #475569; font-size: 13px; font-weight: 900; }
    .hint { color: #64748b; font-size: 12px; font-weight: 700; text-align: center; }
    @media (max-width: 760px) {
      .toolbar { align-items: flex-start; flex-direction: column; }
      .toolbar-actions { justify-content: flex-start; }
      .viewer { grid-template-columns: 1fr 1fr; gap: 10px; padding: 14px; }
      .stage { grid-column: 1 / -1; grid-row: 1; }
      .nav { width: 100%; min-height: 44px; grid-row: 2; }
      .nav.prev { grid-column: 1; }
      .nav.next { grid-column: 2; }
      .card-host { width: min(92vw, 620px); border-radius: 12px; }
    }
    @media print {
      body { background: #fff; }
      .toolbar, .nav, .counter, .hint { display: none; }
      .viewer { min-height: auto; display: block; padding: 0; }
      .stage { display: block; }
      .card-host { width: 100vw; max-width: none; box-shadow: none; border-radius: 0; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-title">
      <strong id="deckTitle">${escapeHtml(deckTitle)}</strong>
      <span>카드별로 확인한 뒤 현재 이미지를 다운로드할 수 있습니다.</span>
    </div>
    <div class="toolbar-actions">
      <span class="counter" id="counter">1 / 1</span>
      <button type="button" class="primary" id="downloadBtn">PNG 다운로드</button>
      <button type="button" id="printBtn">인쇄/PDF</button>
      <button type="button" id="closeBtn">닫기</button>
    </div>
  </div>
  <main class="viewer">
    <button type="button" class="nav prev" id="prevBtn">이전</button>
    <section class="stage">
      <div class="card-host" id="cardHost" aria-live="polite"></div>
      <div class="hint">현재 보이는 카드 1장이 다운로드됩니다.</div>
    </section>
    <button type="button" class="nav next" id="nextBtn">다음</button>
  </main>
  <script>
    (function () {
      var payload = ${cardNewsPayload};
      var cards = Array.isArray(payload.cards) && payload.cards.length ? payload.cards : [{ title: payload.title || '카드뉴스', badge: 'SGEZ', lines: ['표시할 카드뉴스 내용이 없습니다.'] }];
      var index = 0;
      var paletteMap = {
        integrated: { accent: '#0f2a4d', accent2: '#2563eb', soft: '#dbeafe', soft2: '#eff6ff' },
        health: { accent: '#047857', accent2: '#0f766e', soft: '#d1fae5', soft2: '#ecfdf5' },
        hidden: { accent: '#b45309', accent2: '#d97706', soft: '#fef3c7', soft2: '#fffbeb' },
        maturity: { accent: '#6d28d9', accent2: '#7c3aed', soft: '#ede9fe', soft2: '#f5f3ff' }
      };
      var palette = paletteMap[payload.sourceType] || paletteMap.integrated;
      var host = document.getElementById('cardHost');
      var counter = document.getElementById('counter');
      var prevBtn = document.getElementById('prevBtn');
      var nextBtn = document.getElementById('nextBtn');
      var downloadBtn = document.getElementById('downloadBtn');
      var printBtn = document.getElementById('printBtn');
      var closeBtn = document.getElementById('closeBtn');

      function esc(value) {
        return String(value == null ? '' : value).replace(/[&<>"]/g, function (ch) {
          return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch];
        });
      }
      function wrapText(value, limit, maxLines) {
        var text = String(value || '').replace(/\\s+/g, ' ').trim();
        if (!text) return [];
        var words = text.split(' ');
        var lines = [];
        var line = '';
        function pushLongWord(word) {
          var remain = word;
          while (remain.length > limit && (!maxLines || lines.length < maxLines)) {
            lines.push(remain.slice(0, limit));
            remain = remain.slice(limit);
          }
          return remain;
        }
        words.forEach(function (word) {
          if (!word) return;
          var next = line ? line + ' ' + word : word;
          if (next.length <= limit) {
            line = next;
            return;
          }
          if (line) lines.push(line);
          line = word.length > limit ? pushLongWord(word) : word;
        });
        if (line) lines.push(line);
        if (maxLines && lines.length > maxLines) {
          lines = lines.slice(0, maxLines);
          lines[lines.length - 1] = lines[lines.length - 1].replace(/[.。,!！?？\\s]+$/, '') + '…';
        }
        return lines;
      }
      function textSvg(lines, x, y, size, weight, color, lineHeight, anchor) {
        return lines.map(function (line, idx) {
          return '<text x="' + x + '" y="' + (y + idx * lineHeight) + '" text-anchor="' + (anchor || 'start') + '" font-size="' + size + '" font-weight="' + weight + '" fill="' + color + '">' + esc(line) + '</text>';
        }).join('');
      }
      function renderAdvisorCardSvg(card, cardIndex) {
        var advisor = card.advisor || {};
        var name = advisor.name || card.title || '담당 설계사';
        var title = advisor.title || '담당 설계사';
        var orgName = advisor.orgName || '';
        var phone = advisor.phone || '';
        var message = advisor.message || (card.lines && card.lines[0]) || '필요한 확인사항을 함께 정리해드리겠습니다.';
        var imageUrl = advisor.profileImageUrl || '';
        var messageLines = wrapText(message, 21, 3);
        var infoLines = [
          orgName ? orgName + ' · ' + title : title,
          phone ? '연락처 ' + phone : '궁금한 점은 편하게 문의해 주세요.'
        ];
        var initial = String(name || 'SGEZ').trim().slice(0, 1) || 'S';
        var profile = imageUrl
          ? '<defs><clipPath id="advisorPhoto"><circle cx="540" cy="282" r="142"/></clipPath></defs><circle cx="540" cy="282" r="150" fill="#ffffff" stroke="' + palette.accent + '" stroke-width="8"/><image href="' + esc(imageUrl) + '" x="398" y="140" width="284" height="284" preserveAspectRatio="xMidYMid slice" clip-path="url(#advisorPhoto)"/>'
          : '<circle cx="540" cy="282" r="150" fill="' + palette.accent + '"/><circle cx="540" cy="282" r="128" fill="' + palette.soft + '"/><text x="540" y="324" text-anchor="middle" font-size="112" font-weight="900" fill="' + palette.accent + '">' + esc(initial) + '</text>';
        return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1080" role="img" aria-label="' + esc(name) + '">' +
          '<rect width="1080" height="1080" fill="#ffffff"/>' +
          '<rect x="0" y="0" width="1080" height="1080" fill="' + palette.soft2 + '"/>' +
          '<circle cx="930" cy="930" r="220" fill="' + palette.soft + '"/>' +
          '<rect x="60" y="58" width="960" height="964" rx="34" fill="#ffffff" stroke="#dbe3ef" stroke-width="2"/>' +
          '<rect x="360" y="88" width="360" height="48" rx="24" fill="' + palette.soft + '" stroke="' + palette.accent2 + '" stroke-width="1"/>' +
          '<text x="540" y="120" text-anchor="middle" font-size="22" font-weight="900" fill="' + palette.accent + '">담당 설계사 안내</text>' +
          profile +
          '<text x="540" y="518" text-anchor="middle" font-size="56" font-weight="900" fill="#0f172a">' + esc(name) + '</text>' +
          textSvg(infoLines, 540, 572, 27, 800, '#475569', 38, 'middle') +
          '<rect x="126" y="682" width="828" height="156" rx="24" fill="' + palette.soft2 + '" stroke="' + palette.soft + '" stroke-width="2"/>' +
          '<text x="160" y="732" font-size="34" font-weight="900" fill="' + palette.accent + '">“</text>' +
          textSvg(messageLines, 196, 735, 32, 800, '#0f172a', 42) +
          '<text x="920" y="815" text-anchor="end" font-size="34" font-weight="900" fill="' + palette.accent + '">”</text>' +
          '<rect x="88" y="944" width="904" height="1" fill="#e2e8f0"/>' +
          '<text x="88" y="985" font-size="23" font-weight="900" fill="#64748b">SGEZ ' + esc(payload.title || '카드뉴스') + '</text>' +
          '<text x="992" y="985" text-anchor="end" font-size="23" font-weight="900" fill="' + palette.accent + '">' + (cardIndex + 1) + '/' + cards.length + '</text>' +
          '</svg>';
      }
      function renderCardSvg(card, cardIndex) {
        if (card && card.type === 'advisor') return renderAdvisorCardSvg(card, cardIndex);
        var titleLines = wrapText(card.title, 15, 3);
        var badge = card.badge || payload.title || 'SGEZ';
        var lines = (Array.isArray(card.lines) ? card.lines : []).filter(Boolean).slice(0, 4);
        var listY = 308 + Math.max(0, titleLines.length - 1) * 58;
        var lineGroups = '';
        lines.forEach(function (line, rowIndex) {
          var wrapped = wrapText(line, 31, 2);
          var rowY = listY + rowIndex * 118;
          lineGroups += '<rect x="92" y="' + (rowY - 32) + '" width="6" height="' + (wrapped.length > 1 ? 82 : 48) + '" rx="3" fill="' + palette.accent + '"/>';
          lineGroups += textSvg(wrapped, 118, rowY, 31, 800, '#334155', 39);
        });
        return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1080" role="img" aria-label="' + esc(card.title || payload.title) + '">' +
          '<rect width="1080" height="1080" fill="#ffffff"/>' +
          '<rect x="0" y="0" width="1080" height="1080" fill="' + palette.soft2 + '"/>' +
          '<circle cx="950" cy="945" r="208" fill="' + palette.soft + '"/>' +
          '<circle cx="100" cy="90" r="74" fill="' + palette.soft + '" opacity=".75"/>' +
          '<rect x="60" y="58" width="960" height="964" rx="34" fill="#ffffff" stroke="#dbe3ef" stroke-width="2"/>' +
          '<rect x="88" y="88" width="272" height="48" rx="24" fill="' + palette.soft + '" stroke="' + palette.accent2 + '" stroke-width="1"/>' +
          '<text x="224" y="120" text-anchor="middle" font-size="22" font-weight="900" fill="' + palette.accent + '">' + esc(badge) + '</text>' +
          textSvg(titleLines, 88, 220, 56, 900, '#0f172a', 66) +
          '<rect x="88" y="' + (listY - 72) + '" width="120" height="6" rx="3" fill="' + palette.accent + '"/>' +
          lineGroups +
          '<rect x="88" y="944" width="904" height="1" fill="#e2e8f0"/>' +
          '<text x="88" y="985" font-size="23" font-weight="900" fill="#64748b">SGEZ ' + esc(payload.title || '카드뉴스') + '</text>' +
          '<text x="992" y="985" text-anchor="end" font-size="23" font-weight="900" fill="' + palette.accent + '">' + (cardIndex + 1) + '/' + cards.length + '</text>' +
          '</svg>';
      }
      function render() {
        host.innerHTML = renderCardSvg(cards[index], index);
        counter.textContent = (index + 1) + ' / ' + cards.length;
        prevBtn.disabled = index <= 0;
        nextBtn.disabled = index >= cards.length - 1;
      }
      function safeFileName(value) {
        return String(value || 'card-news').replace(/[\\\\/:*?"<>|]+/g, '_').replace(/\\s+/g, '_').slice(0, 80);
      }
      function downloadCurrentPng() {
        var svgNode = host.querySelector('svg');
        if (!svgNode) return;
        var svgText = new XMLSerializer().serializeToString(svgNode);
        var blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var image = new Image();
        image.onload = function () {
          var canvas = document.createElement('canvas');
          canvas.width = 1080;
          canvas.height = 1080;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          canvas.toBlob(function (pngBlob) {
            if (!pngBlob) return;
            var link = document.createElement('a');
            var downloadUrl = URL.createObjectURL(pngBlob);
            link.href = downloadUrl;
            link.download = safeFileName((payload.title || '카드뉴스') + '_' + (index + 1)) + '.png';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(function () { URL.revokeObjectURL(downloadUrl); }, 1000);
          }, 'image/png');
        };
        image.onerror = function () { URL.revokeObjectURL(url); };
        image.src = url;
      }
      prevBtn.addEventListener('click', function () {
        if (index > 0) {
          index -= 1;
          render();
        }
      });
      nextBtn.addEventListener('click', function () {
        if (index < cards.length - 1) {
          index += 1;
          render();
        }
      });
      downloadBtn.addEventListener('click', downloadCurrentPng);
      printBtn.addEventListener('click', function () { window.print(); });
      closeBtn.addEventListener('click', function () { window.close(); });
      window.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') prevBtn.click();
        if (event.key === 'ArrowRight') nextBtn.click();
      });
      render();
    })();
  </script>
</body>
</html>`
}

export function openCustomerCardNewsWindow(data = {}, options = {}) {
  const popup = window.open('about:blank', '_blank', options.windowFeatures || 'width=720,height=820')
  if (!popup) return false
  popup.document.open()
  popup.document.write(buildCustomerCardNewsHtml(data))
  popup.document.close()
  popup.focus()
  return true
}
